import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { PartnerFilterSelect } from 'src/components/payments-ops/PartnerFilterSelect'
import { ToolbarCheckbox } from 'src/components/payments-ops/ToolbarCheckbox'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { FeeWithdrawalStatus, FeeWithdrawalType } from 'src/constants/payments-ops/enums'
import {
  feeWithdrawalStatusLabels,
  feeWithdrawalTypeLabels,
} from 'src/constants/payments-ops/labels'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { FeeWithdrawalFilters } from 'src/interfaces/feeWithdrawal'

interface Props {
  filters: FeeWithdrawalFilters
  hasReportOnly: boolean
  onFiltersChange(updates: Partial<FeeWithdrawalFilters>): void
  onHasReportOnlyChange(hasReportOnly: boolean): void
  onRefresh(): void
}

const ANY_VALUE = ''

const FeeWithdrawalsToolbarComponent = ({
  filters,
  hasReportOnly,
  onFiltersChange,
  onHasReportOnlyChange,
  onRefresh,
}: Props) => {
  // The options come from `GET /partners` (`partners_view`), which PaymentsOpsLayout skips without
  // it — an "All partners"-only select is useless, so the filter is hidden (spec §6).
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  const statusOptions = useMemo(() => {
    const options = Object.values(FeeWithdrawalStatus).map((value) => {
      return { value, label: feeWithdrawalStatusLabels[value].label }
    })

    return [{ value: ANY_VALUE, label: 'Any status' }, ...options]
  }, [])

  const typeOptions = useMemo(() => {
    const options = Object.values(FeeWithdrawalType).map((value) => {
      return { value, label: feeWithdrawalTypeLabels[value] }
    })

    return [{ value: ANY_VALUE, label: 'Any type' }, ...options]
  }, [])

  const handleStatusChange = useCallback(
    (value: string | number) => {
      onFiltersChange({ status: value === ANY_VALUE ? null : (value as FeeWithdrawalStatus) })
    },
    [onFiltersChange],
  )

  const handleTypeChange = useCallback(
    (value: string | number) => {
      onFiltersChange({ type: value === ANY_VALUE ? null : (value as FeeWithdrawalType) })
    },
    [onFiltersChange],
  )

  const handlePartnerChange = useCallback(
    (partnerId: string | null) => {
      onFiltersChange({ partnerId })
    },
    [onFiltersChange],
  )

  return (
    <ToolbarRow>
      <div css={tw`flex w-[180px] flex-col`}>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Status
        </Typography>
        <ProtoSelectStandalone
          value={filters.status ?? ANY_VALUE}
          options={statusOptions}
          onValueChange={handleStatusChange}
        />
      </div>
      <div css={tw`flex w-[220px] flex-col`}>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Type
        </Typography>
        <ProtoSelectStandalone
          value={filters.type ?? ANY_VALUE}
          options={typeOptions}
          onValueChange={handleTypeChange}
        />
      </div>
      {canViewPartners ? (
        <PartnerFilterSelect value={filters.partnerId} onChange={handlePartnerChange} />
      ) : null}
      <Tooltip title="Filters the rows on this page only">
        <div>
          <ToolbarCheckbox
            name="feeWithdrawalsHasReport"
            label="Has report"
            checked={hasReportOnly}
            onChange={onHasReportOnlyChange}
          />
        </div>
      </Tooltip>
      <div css={tw`ml-auto flex flex-row gap-2 pb-1`}>
        <Button
          size="small"
          color="white"
          leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
          onClick={onRefresh}
          data-testid="fee-withdrawals-refresh"
        >
          Refresh
        </Button>
      </div>
    </ToolbarRow>
  )
}

export const FeeWithdrawalsToolbar = memo(FeeWithdrawalsToolbarComponent)
